import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Button, Text, VoiceSelector } from "@core/index";
import swannLoading from "@packages/assets/swannloading.gif";
import { CEFRSelector } from "@core/cefr-selector";
import OutputLanguageSelector from "@features/tools/components/create-modal/OutputLanguageSelector";
import enFlag from "@packages/assets/en.png";
import deFlag from "@packages/assets/de.png";
import { useCreateSpokenDialogue } from "@packages/api/madlenAPI";
import { useGetSpokenDialogue } from "@packages/api/madlenAPI";
import {
  SpokenDialogueCreateRequestDtoCefrLevel,
  SpokenDialogueCreateRequestDtoLanguage,
  CreatePronunciationAssignmentRequestDtoAccent,
} from "@packages/api/madlenAPI.schemas";
import { getVoicesByLanguage } from "@packages/config/elevenlabsConfig";

type LanguageCode = "en" | "de";
type Accent = CreatePronunciationAssignmentRequestDtoAccent;

const CRITERIA_KEYS = [
  "accent",
  "listening",
  "speaking",
  "sentenceConstruction",
  "vocabulary",
  "confidence",
] as const;

const CreateSpokenDialogueView = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [scenario, setScenario] = useState<string>("");
  const [language, setLanguage] = useState<LanguageCode>("en");
  const [accent, setAccent] = useState<Accent>("AMERICAN");
  const [selectedVoiceId, setSelectedVoiceId] = useState<string>("");
  const [dialogueCount, setDialogueCount] = useState<number>(6); // 4-20
  const [cefrLevel, setCefrLevel] = useState<any>(
    // CEFRSelector expects CreatePodcastRequestDtoCefrLevel enum; values align numerically
    // We intentionally keep a generic type here to avoid strict coupling to a specific DTO
    0,
  );

  const [weights, setWeights] = useState<
    Record<(typeof CRITERIA_KEYS)[number], number>
  >({
    accent: 20,
    listening: 20,
    speaking: 20,
    sentenceConstruction: 20,
    vocabulary: 20,
    confidence: 0,
  });

  function redistributeWeights(
    prev: Record<(typeof CRITERIA_KEYS)[number], number>,
    updatedKey: (typeof CRITERIA_KEYS)[number],
    newValueInput: number,
  ): Record<(typeof CRITERIA_KEYS)[number], number> {
    // Clamp each bar to a maximum of 50%
    const newValue = Math.max(0, Math.min(50, Math.round(newValueInput)));
    const otherKeys = CRITERIA_KEYS.filter((k) => k !== updatedKey);

    const next: Record<(typeof CRITERIA_KEYS)[number], number> = {
      accent: 0,
      listening: 0,
      speaking: 0,
      sentenceConstruction: 0,
      vocabulary: 0,
      confidence: 0,
    } as any;
    next[updatedKey] = newValue;

    let remaining = 100 - newValue;

    // Determine proportional targets based on previous values to keep feel similar
    const prevOthersSum = otherKeys.reduce((sum, k) => sum + (prev[k] || 0), 0);
    const desired: Record<(typeof CRITERIA_KEYS)[number], number> = {} as any;
    if (prevOthersSum > 0) {
      otherKeys.forEach((k) => {
        const target = (prev[k] * remaining) / prevOthersSum;
        desired[k] = Math.min(50, Math.max(0, target));
      });
    } else {
      const even = remaining / otherKeys.length;
      otherKeys.forEach((k) => {
        desired[k] = Math.min(50, Math.max(0, even));
      });
    }

    // Seed floors
    let sumFloors = 0;
    otherKeys.forEach((k) => {
      const base = Math.floor(desired[k]);
      next[k] = Math.min(50, base);
      sumFloors += next[k];
    });

    let deficit = remaining - sumFloors;

    // Order by fractional part desc, then by previous weight desc, then CRITERIA order
    const order = [...otherKeys].sort((a, b) => {
      const fracA = desired[a] - Math.floor(desired[a] || 0);
      const fracB = desired[b] - Math.floor(desired[b] || 0);
      if (fracB !== fracA) return fracB - fracA;
      const prevDiff = (prev[b] || 0) - (prev[a] || 0);
      if (prevDiff !== 0) return prevDiff;
      return CRITERIA_KEYS.indexOf(a) - CRITERIA_KEYS.indexOf(b);
    });

    // Distribute remaining 1 by 1 respecting 50% cap
    while (deficit > 0) {
      let allocated = false;
      for (const k of order) {
        if (deficit <= 0) break;
        if ((next[k] || 0) < 50) {
          next[k] += 1;
          deficit -= 1;
          allocated = true;
        }
      }
      // Safety: if nothing could be allocated (all at 50), break to avoid infinite loop
      if (!allocated) break;
    }

    return next;
  }

  const totalWeight = Object.values(weights).reduce((a, b) => a + b, 0);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const quickPresets = [4, 6, 8, 10, 12, 15, 20];

  // Auto-select first available voice when language/accent changes or component loads
  useEffect(() => {
    if (language === "en") {
      const languageKey = accent === "AMERICAN" ? "en_us" : "en_gb";
      const availableVoices = getVoicesByLanguage(languageKey);
      if (availableVoices.length > 0) {
        // Check if current selected voice is valid for this language/accent
        const currentVoice = availableVoices.find(
          (voice) => voice.voiceId === selectedVoiceId,
        );
        if (!currentVoice) {
          setSelectedVoiceId(availableVoices[0].voiceId);
        }
      }
    } else if (language === "de") {
      const availableVoices = getVoicesByLanguage("de_de");
      if (availableVoices.length > 0) {
        // Check if current selected voice is valid for German
        const currentVoice = availableVoices.find(
          (voice) => voice.voiceId === selectedVoiceId,
        );
        if (!currentVoice) {
          setSelectedVoiceId(availableVoices[0].voiceId);
        }
      }
    }
  }, [language, accent, selectedVoiceId]);

  function validate(): boolean {
    const e: Record<string, string> = {};
    if (!(scenario || "").trim()) e.scenario = t("common.required");
    if (dialogueCount < 4 || dialogueCount > 20)
      e.dialogueCount = t("common.error");
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  const createMutation = useCreateSpokenDialogue();
  const { mutateAsync: createSpokenDialogueMutate } = createMutation;
  const isCreating = createMutation.status === "pending";
  const [createdId, setCreatedId] = useState<number | null>(null);

  // Poll the created resource until it becomes READY or FAILED
  const { data: createdData } = useGetSpokenDialogue(createdId ?? 0, {
    query: {
      enabled: !!createdId,
      refetchInterval: (d: any) => {
        const s = (d as any)?.status as string | undefined;
        if (s === "READY" || s === "FAILED") return false;
        return 3000;
      },
      refetchOnWindowFocus: false,
    },
  });

  // Navigate to the detail page when resource becomes READY
  useEffect(() => {
    if (!createdId) return;
    const s = (createdData as any)?.status as string | undefined;
    if (s === "READY") {
      // navigate to detail with the same state we previously used
      navigate(`/content/spoken-dialogue/${createdId}`, {
        state: {
          scenario,
          language,
          accent,
          selectedVoiceId,
          dialogueCount,
          cefrLevel,
          weights,
          loadingReview: true,
        },
      });
    }
    // if FAILED, we simply stop polling and let user decide; global errors handled elsewhere
  }, [createdData, createdId]);

  async function handleSubmit() {
    if (!validate()) return;
    try {
      const payload = {
        scenario,
        language:
          language as keyof typeof SpokenDialogueCreateRequestDtoLanguage as any,
        accent,
        voice_id: selectedVoiceId,
        duration: dialogueCount,
        cefr_level:
          cefrLevel as unknown as keyof typeof SpokenDialogueCreateRequestDtoCefrLevel as any,
        evaluation_criteria: weights as Record<string, number>,
      };
      const result = await createSpokenDialogueMutate({ data: payload });
      const id = (result as any)?.id;
      if (id) {
        // Start polling the created resource and show loading overlay until READY
        setCreatedId(Number(id));
      }
    } catch (e) {
      // error toasts handled globally in axiosConfig
    }
  }

  return (
    <div className="flex flex-col h-full min-h-0 bg-gray-50 dark:bg-dark-500 mt-5 rounded-tl-3xl">
      {/* Full-screen loading overlay after creation until status READY/FAILED (only image, no component) */}
      {createdId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 dark:bg-black/60">
          <div className="bg-white dark:bg-dark-600 rounded-xl shadow-lg p-6 flex flex-col items-center">
            {(() => {
              const s = (createdData as any)?.status as string | undefined;
              const title = s
                ? t(`spokenDialogue.status.${s.toLowerCase()}`)
                : t("spokenDialogue.create.creating");
              let desc = t("spokenDialogue.create.creatingDescription");
              if (s && s.toUpperCase() === "FAILED") {
                desc = t("spokenDialogue.create.failedDescription");
              } else if (
                s &&
                s.toUpperCase() !== "READY" &&
                s.toUpperCase() !== "FAILED"
              ) {
                desc = t("spokenDialogue.create.processingDescription");
              }

              return (
                <>
                  <img
                    src={swannLoading}
                    alt="Loading"
                    className="w-36 h-36 object-contain mb-4"
                  />
                  <div className="text-center">
                    <Text
                      variant="h2"
                      className="text-lg font-semibold text-gray-900 dark:text-gray-100"
                    >
                      {title}
                    </Text>
                    <Text
                      variant="p"
                      className="text-sm text-gray-600 dark:text-gray-400 mt-2"
                    >
                      {desc}
                    </Text>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}
      <div className="flex items-center px-6 pt-8 pb-4">
        <button
          onClick={() => navigate("/content/spoken-dialogue")}
          className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mr-4 transition-colors"
        ></button>
        <Text variant="h1" className="text-2xl font-semibold flex-1">
          {t("spokenDialogue.create.title")}
        </Text>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto px-6 py-8">
        <div className="max-w-4xl mx-auto space-y-4">
          {/* Scenario */}
          <div className="bg-white dark:bg-dark-600 rounded-xl shadow-sm border border-gray-200 dark:border-dark-400 p-6">
            <div className="mb-4">
              <Text
                variant="h2"
                className="text-lg font-semibold text-gray-900 dark:text-gray-100"
              >
                {t("spokenDialogue.create.scenario.title")}
              </Text>
              <Text
                variant="p"
                className="text-sm text-gray-600 dark:text-gray-400"
              >
                {t("spokenDialogue.create.scenario.description")}
              </Text>
            </div>
            <textarea
              value={scenario}
              onChange={(e) => setScenario(e.target.value)}
              placeholder={t("spokenDialogue.create.scenario.placeholder")}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-dark-500 dark:border-dark-500 dark:text-white transition-colors ${
                errors.scenario
                  ? "border-red-500"
                  : "border-gray-300 dark:border-dark-400"
              }`}
              rows={4}
            />
            {errors.scenario && (
              <Text variant="p" className="text-red-500 text-sm mt-2">
                {errors.scenario}
              </Text>
            )}
          </div>

          {/* CEFR Level */}
          <div className="bg-white dark:bg-dark-600 rounded-xl shadow-sm border border-gray-200 dark:border-dark-400 p-6">
            <div className="mb-3">
              <Text
                variant="h2"
                className="text-base font-semibold text-gray-900 dark:text-gray-100"
              >
                {t("spokenDialogue.create.cefrLevel.title")}
              </Text>
              <Text
                variant="p"
                className="text-xs text-gray-600 dark:text-gray-400"
              >
                {t("spokenDialogue.create.cefrLevel.description")}
              </Text>
            </div>
            <CEFRSelector
              selectedLevel={cefrLevel}
              onLevelChange={(level) => setCefrLevel(level)}
              themeColor="blue"
              className="mt-1"
            />
          </div>

          {/* Language and Accent */}
          <div className="bg-white dark:bg-dark-600 rounded-xl shadow-sm border border-gray-200 dark:border-dark-400 p-6">
            <Text
              variant="h2"
              className="text-lg font-semibold mb-6 text-gray-900 dark:text-gray-100"
            >
              {t("spokenDialogue.create.language")} &{" "}
              {t("voiceSelector.selectVoice")}
            </Text>
            {language === "de" ? (
              // German layout: Language and Voice side by side
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
                <div className="mt-3">
                  <OutputLanguageSelector
                    selectedLanguage={language}
                    onLanguageChange={(lang: string) =>
                      setLanguage((lang as LanguageCode) || "en")
                    }
                    themeColor="blue"
                    languages={[
                      { code: "en", name: "English", flag: enFlag },
                      { code: "de", name: "Deutsch", flag: deFlag },
                    ]}
                  />
                </div>
                <div className="mt-2">
                  <VoiceSelector
                    selectedVoiceId={selectedVoiceId}
                    onVoiceChange={setSelectedVoiceId}
                    language={language}
                    accent={accent}
                    themeColor="blue"
                    className="mt-1"
                  />
                </div>
              </div>
            ) : (
              // English layout: Language and Accent side by side, Voice below
              <div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
                  <div className="mt-3">
                    <OutputLanguageSelector
                      selectedLanguage={language}
                      onLanguageChange={(lang: string) =>
                        setLanguage((lang as LanguageCode) || "en")
                      }
                      themeColor="blue"
                      languages={[
                        { code: "en", name: "English", flag: enFlag },
                        { code: "de", name: "Deutsch", flag: deFlag },
                      ]}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3 mt-3">
                    <button
                      type="button"
                      onClick={() => setAccent("AMERICAN")}
                      className={`flex items-center justify-center gap-3 rounded-lg border-2 transition-all duration-200 ${
                        accent === "AMERICAN"
                          ? "border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20 shadow-sm"
                          : "border-gray-200 dark:border-dark-400 hover:border-gray-300 dark:hover:border-dark-300 hover:shadow-sm"
                      }`}
                    >
                      <span className="text-2xl">🇺🇸</span>
                      <span
                        className={`${accent === "AMERICAN" ? "text-indigo-700 dark:text-indigo-300" : "text-gray-900 dark:text-gray-100"} text-sm font-medium`}
                      >
                        {t("pronunciationAssessment.create.accent.american")}
                      </span>
                    </button>

                    <button
                      type="button"
                      onClick={() => setAccent("BRITISH")}
                      className={`flex items-center justify-center gap-3 rounded-lg border-2 transition-all duration-200 ${
                        accent === "BRITISH"
                          ? "border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20 shadow-sm"
                          : "border-gray-200 dark:border-dark-400 hover:border-gray-300 dark:hover:border-dark-300 hover:shadow-sm"
                      }`}
                    >
                      <span className="text-2xl">🇬🇧</span>
                      <span
                        className={`${accent === "BRITISH" ? "text-indigo-700 dark:text-indigo-300" : "text-gray-900 dark:text-gray-100"} text-sm font-medium`}
                      >
                        {t("pronunciationAssessment.create.accent.british")}
                      </span>
                    </button>
                  </div>
                </div>

                {/* Voice Selection - Below for English */}
                <div className="pt-4">
                  <VoiceSelector
                    selectedVoiceId={selectedVoiceId}
                    onVoiceChange={setSelectedVoiceId}
                    language={language}
                    accent={accent}
                    themeColor="blue"
                    className="mt-1"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Duration / Dialogue count */}
          <div className="bg-white dark:bg-dark-600 rounded-xl shadow-sm border border-gray-200 dark:border-dark-400 p-6">
            <Text
              variant="h2"
              className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-100"
            >
              {t("spokenDialogue.create.duration.title")}
            </Text>
            <Text
              variant="p"
              className="text-sm text-gray-600 dark:text-gray-400 mb-4"
            >
              {t("spokenDialogue.create.duration.description")}
            </Text>
            <div className="flex items-center justify-between mb-3 gap-3">
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {t("spokenDialogue.create.duration.min")}: 4
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="px-3 py-2 rounded-lg border border-gray-300 dark:border-dark-400 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-dark-500"
                  onClick={() =>
                    setDialogueCount((c) => Math.max(4, Math.min(20, c - 1)))
                  }
                >
                  −
                </button>
                <input
                  type="number"
                  min={4}
                  max={20}
                  value={dialogueCount}
                  onChange={(e) => {
                    const v = Number(e.target.value);
                    if (!Number.isNaN(v)) {
                      setDialogueCount(Math.max(4, Math.min(20, v)));
                    }
                  }}
                  className="w-20 text-center px-3 py-2 rounded-lg border border-gray-300 dark:border-dark-400 bg-white dark:bg-dark-500 text-gray-900 dark:text-white"
                />
                <button
                  type="button"
                  className="px-3 py-2 rounded-lg border border-gray-300 dark:border-dark-400 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-dark-500"
                  onClick={() =>
                    setDialogueCount((c) => Math.max(4, Math.min(20, c + 1)))
                  }
                >
                  +
                </button>
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {t("spokenDialogue.create.duration.max")}: 20
              </div>
            </div>
            <input
              type="range"
              min={4}
              max={20}
              value={dialogueCount}
              onChange={(e) => setDialogueCount(Number(e.target.value))}
              className="w-full h-2 bg-gradient-to-r from-indigo-200 to-gray-200 dark:from-indigo-800 dark:to-gray-600 rounded-full appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-dark-600"
            />
            <div className="mt-3 flex flex-wrap gap-2">
              {quickPresets.map((v) => (
                <button
                  key={v}
                  type="button"
                  className={`${
                    dialogueCount === v
                      ? "bg-indigo-600 text-white"
                      : "bg-gray-100 dark:bg-dark-500 text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-dark-400"
                  } px-3 py-1.5 rounded-full text-sm`}
                  onClick={() => setDialogueCount(v)}
                >
                  {v}
                </button>
              ))}
            </div>
            <div className="mt-2 text-xs text-gray-600 dark:text-gray-400">
              {t("spokenDialogue.create.duration.estimated", {
                minutes: Math.max(1, Math.round(dialogueCount * 0.5)),
              })}
            </div>
            {errors.dialogueCount && (
              <Text variant="p" className="text-red-500 text-sm mt-2">
                {errors.dialogueCount}
              </Text>
            )}
          </div>

          {/* Evaluation Criteria */}
          <div className="bg-white dark:bg-dark-600 rounded-xl shadow-sm border border-gray-200 dark:border-dark-400 p-6 hidden">
            <Text
              variant="h2"
              className="text-lg font-semibold mb-2 text-gray-900 dark:text-gray-100"
            >
              {t("spokenDialogue.create.criteria.title")}
            </Text>
            <Text
              variant="p"
              className="text-sm text-gray-600 dark:text-gray-400 mb-4"
            >
              {t("spokenDialogue.create.criteria.description")}
            </Text>

            <div className="space-y-3">
              {CRITERIA_KEYS.map((key) => (
                <div
                  key={key}
                  className="grid grid-cols-1 sm:grid-cols-[1fr,auto,200px] items-center gap-3 p-3 rounded-lg bg-gray-50 dark:bg-dark-500 border border-gray-200 dark:border-dark-400"
                >
                  <div className="font-medium text-gray-900 dark:text-gray-100">
                    {t(`spokenDialogue.create.criteria.${key}`)}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {t("spokenDialogue.create.criteria.weight")}
                  </div>
                  <div className="flex items-center gap-3">
                    <input
                      type="range"
                      min={0}
                      max={50}
                      value={weights[key]}
                      onChange={(e) =>
                        setWeights((prev) =>
                          redistributeWeights(
                            prev,
                            key,
                            Number(e.target.value),
                          ),
                        )
                      }
                      className="flex-1 h-2 bg-gradient-to-r from-indigo-300 to-gray-200 dark:from-indigo-700 dark:to-gray-600 rounded-full appearance-none cursor-pointer"
                    />
                    <div className="w-14 text-right text-sm font-semibold text-gray-900 dark:text-gray-100">
                      {weights[key]}%
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-3 text-xs text-gray-600 dark:text-gray-400">
              {t("spokenDialogue.create.criteria.total")}: {totalWeight}%
            </div>
          </div>

          <div className="flex justify-end space-x-3">
            <Button
              variant="outline"
              size="md"
              rounded="sm"
              onClick={() => navigate("/content/spoken-dialogue")}
              className="border-gray-300 dark:border-gray-600"
            >
              {t("common.cancel")}
            </Button>
            <Button
              size="md"
              rounded="sm"
              onClick={handleSubmit}
              className="bg-gradient-to-r from-indigo-500 to-indigo-600 hover:from-indigo-600 hover:to-indigo-700 text-white"
              disabled={isCreating}
            >
              {t("spokenDialogue.create.submit")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateSpokenDialogueView;
